import axios from 'axios';
import errorHandler from './errorHandler';

const GUEST_ID_STORAGE_KEY = 'research-guest-id';
const STREAM_INACTIVITY_TIMEOUT_MS = 600000;
const DEFAULT_DEEP_RESEARCH_CONFIG = {
  report_type: 'deep',
  deep_research_breadth: 2,
  deep_research_depth: 2,
};

// 构建前端显式发送的深度研究配置，允许调用方覆盖默认值。
const buildDeepResearchConfig = (config = {}) => ({
  ...DEFAULT_DEEP_RESEARCH_CONFIG,
  ...config,
});

// 构建研究执行请求，只有确认计划存在时才带 plan_items。
const buildResearchRequestPayload = (query, stream, options = {}) => {
  const payload = {
    query,
    stream,
    config: buildDeepResearchConfig(options.config),
  };
  if (options.planItems) {
    payload.plan_items = options.planItems;
  }
  return payload;
};

// 解析 API 地址，避免生产环境静默回退到访问者本机。
const resolveApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (window.location.hostname === 'localhost') {
    return 'http://localhost:8003';
  }
  if (window.location.hostname === '127.0.0.1') {
    return 'http://127.0.0.1:8003';
  }
  if (window.location.origin.includes('railway.app')) {
    return `${window.location.protocol}//${window.location.hostname}`;
  }
  return window.location.origin;
};

const API_BASE_URL = resolveApiBaseUrl();

export const getGuestId = () => {
  const existingGuestId = localStorage.getItem(GUEST_ID_STORAGE_KEY);
  if (existingGuestId) {
    return existingGuestId;
  }

  const generatedGuestId = window.crypto?.randomUUID?.()
    || `guest-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
  localStorage.setItem(GUEST_ID_STORAGE_KEY, generatedGuestId);
  return generatedGuestId;
};

const buildGuestHeaders = () => {
  const guestId = getGuestId();
  return guestId ? { 'X-Guest-Id': guestId } : {};
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000, // 5分钟超时
  headers: {
    'Content-Type': 'application/json',
  },
});

// 添加统一的错误处理
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const handledError = errorHandler.handleApiError(error);
    errorHandler.logError(handledError);
    return Promise.reject(handledError);
  }
);

// 请求拦截器：自动附加 JWT token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    config.headers['X-Guest-Id'] = getGuestId();
    return config;
  },
  (error) => Promise.reject(error)
);


// 研究API
export const researchAPI = {
  streamRequest: async (url, payload, onUpdate, options = {}) => {
    const controller = new AbortController();
    let timedOut = false;
    let timeoutId = null;

    const resetTimeout = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, STREAM_INACTIVITY_TIMEOUT_MS);
    };

    resetTimeout();

    const handleExternalAbort = () => controller.abort();
    if (options.signal) {
      if (options.signal.aborted) {
        controller.abort();
      } else {
        options.signal.addEventListener('abort', handleExternalAbort, { once: true });
      }
    }

    const token = localStorage.getItem('access_token');
    const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};

    try {
      const response = await fetch(`${API_BASE_URL}${url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Connection': 'keep-alive',
          ...authHeaders,
          ...buildGuestHeaders(),
        },
        body: JSON.stringify(payload),
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      if (!response.body) {
        throw new Error('流式响应不可用');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let pendingData = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;
        resetTimeout();

        buffer += decoder.decode(value, { stream: true });

        let eventSeparatorIndex;
        while ((eventSeparatorIndex = buffer.indexOf('\n\n')) !== -1) {
          const rawEvent = buffer.slice(0, eventSeparatorIndex).trim();
          buffer = buffer.slice(eventSeparatorIndex + 2);

          if (!rawEvent) continue;

          const dataLines = rawEvent
            .split('\n')
            .filter(line => line.startsWith('data:'))
            .map(line => line.replace(/^data:\s*/, ''));

          if (dataLines.length === 0) {
            continue;
          }

          const candidate = pendingData ? `${pendingData}${dataLines.join('\n').trim()}` : dataLines.join('\n').trim();

          try {
            const data = JSON.parse(candidate);
            pendingData = '';
            onUpdate(data);
          } catch (error) {
            pendingData = candidate;
          }
        }
      }
    } catch (error) {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      if (error.name === 'AbortError') {
        throw new Error(timedOut ? '请求超时，请重试' : '研究已停止');
      } else if (error.message.includes('Failed to fetch') ||
                 error.message.includes('NetworkError') ||
                 error.message.includes('ERR_CONNECTION_CLOSED')) {
        throw new Error('网络连接失败，请检查网络连接或稍后重试');
      } else {
        throw error;
      }
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (options.signal) {
        options.signal.removeEventListener('abort', handleExternalAbort);
      }
    }
  },

  // 开始研究（流式）
  startResearchStream: async (query, onUpdate, options = {}) => {
    return researchAPI.streamRequest(
      '/api/research',
      buildResearchRequestPayload(query, true, options),
      onUpdate,
      options,
    );
  },

  resumeResearchStream: async (taskId, onUpdate, options = {}) => {
    return researchAPI.streamRequest('/api/research/resume', {
      task_id: taskId,
      stream: true
    }, onUpdate, options);
  },

  stopResearch: async (taskId) => {
    const response = await api.post('/api/research/stop', {
      task_id: taskId,
    });
    return response.data;
  },

  // 开始研究（非流式）
  startResearch: async (query, options = {}) => {
    const response = await errorHandler.withRetry(async () => {
      return await api.post(
        '/api/research',
        buildResearchRequestPayload(query, false, options),
      );
    });
    return response.data;
  },

  previewResearchPlan: async (query, options = {}) => {
    const response = await api.post('/api/research/plan', {
      query,
      config: buildDeepResearchConfig(options.config),
    });
    return response.data;
  },

  // 获取研究状态
  getResearchStatus: async () => {
    const response = await api.get('/api/research/status');
    return response.data;
  },

  // 获取研究历史
  getResearchHistory: async () => {
    const response = await api.get('/api/research/history');
    return response.data;
  },

  getResearchTask: async (taskId) => {
    const response = await api.get(`/api/research/${taskId}`);
    return response.data;
  },

  // 清空研究历史
  clearResearchHistory: async () => {
    const response = await api.delete('/api/research/history');
    return response.data;
  },

  // 健康检查
  healthCheck: async () => {
    const response = await api.get('/api/health');
    return response.data;
  }
};

// 认证API
export const authAPI = {
  googleLoginUrl: () => `${API_BASE_URL}/api/auth/google/login`,

  register: async (email, password) => {
    const response = await api.post('/api/auth/register', { email, password });
    return response.data;
  },

  login: async (email, password) => {
    const response = await api.post('/api/auth/login', { email, password });
    return response.data;
  },

  getMe: async () => {
    const response = await api.get('/api/auth/me');
    return response.data;
  },

  claimHistory: async (taskIds = [], guestId = getGuestId()) => {
    const response = await api.post('/api/auth/claim-history', {
      task_ids: taskIds,
      guest_id: guestId,
    });
    return response.data;
  },
};

export default api;
